/**
 * ViewSwitcher — top-of-board segmented control (Kanban / List / Gantt).
 *
 * Phase 12 / M10. The current selection persists to
 * chaos.user_view_preferences via the parent route's mutation; the
 * switcher itself is purely presentational.
 */
import type { ViewType } from '@chaos/types/views';
import { BarChart3, Columns3, List } from 'lucide-react';

import { cn } from '../../lib/utils';

interface Option {
  value: ViewType;
  label: string;
  icon: typeof Columns3;
}

const OPTIONS: readonly Option[] = [
  { value: 'kanban', label: 'Kanban', icon: Columns3 },
  { value: 'list', label: 'List', icon: List },
  { value: 'gantt', label: 'Gantt', icon: BarChart3 },
];

export interface ViewSwitcherProps {
  value: ViewType;
  onChange: (next: ViewType) => void;
  className?: string;
}

export function ViewSwitcher({ value, onChange, className }: ViewSwitcherProps) {
  return (
    <div
      role="tablist"
      aria-label="Board view"
      className={cn('inline-flex rounded border border-border bg-background text-xs', className)}
    >
      {OPTIONS.map((opt, i) => {
        const Icon = opt.icon;
        const active = value === opt.value;
        return (
          <button
            type="button"
            key={opt.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 px-2 transition-colors',
              active
                ? 'bg-foreground/[0.06] text-foreground'
                : 'text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground',
              i !== OPTIONS.length - 1 && 'border-r border-border',
            )}
          >
            <Icon size={11} aria-hidden />
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
